// Repair the specific accessibility defects in the installed Yotpo widgets and
// Shopify Forms, plus empty editor artifacts in migrated rich text. Keep vendor
// click handlers, authored content, and form behavior intact.
const formStyleId = 'marleylilly-form-accessibility';
const keyboardButtons = new WeakSet();
const pendingForms = new WeakSet();

function repairForm(host) {
  if (host.shadowRoot) {
    if (host.shadowRoot.getElementById(formStyleId)) return;
    const style = host.ownerDocument.createElement('style');
    style.id = formStyleId;
    style.textContent = `
      :host { --form-placeholder-color: #595959; }
      label:has(> input[type="radio"]) {
        grid-template-columns: 24px minmax(0, 1fr);
        min-height: 24px;
      }
      input[type="radio"] { min-width: 24px; min-height: 24px; }
    `;
    host.shadowRoot.append(style);
    return;
  }
  if (pendingForms.has(host)) return;
  pendingForms.add(host);
  // Forms attaches its open shadow root after the host is inserted. Bound the
  // wait so a blocked or unavailable vendor script cannot leave a timer running.
  let attempts = 0;
  const retry = () => {
    if (!host.isConnected || ++attempts > 100) {
      pendingForms.delete(host);
      return;
    }
    if (host.shadowRoot) {
      pendingForms.delete(host);
      repairForm(host);
    } else {
      host.ownerDocument.defaultView.setTimeout(retry, 100);
    }
  };
  host.ownerDocument.defaultView.setTimeout(retry, 100);
}

export function repairThirdPartyAccessibility(root) {
  const find = (selector) => [
    ...(root.matches?.(selector) ? [root] : []),
    ...root.querySelectorAll(selector),
  ];
  for (const marker of find('.rte .rangySelectionBoundary')) {
    if (!marker.textContent.trim()) marker.remove();
  }
  for (const link of find('.rte a[href]')) {
    if (!link.childElementCount && !link.textContent.trim()
      && [...link.attributes].every(({ name }) => ['href', 'target', 'rel'].includes(name))) {
      link.remove();
    }
  }
  for (const heading of find('.yotpo-slider-title[role="heading"]:not([aria-level])')) {
    heading.setAttribute('aria-level', '2');
  }
  for (const button of find('.yotpo-widget-spotlight-cta[aria-role="button"]')) {
    button.removeAttribute('aria-role');
    button.setAttribute('role', 'button');
    button.setAttribute('tabindex', '0');
    const inner = button.querySelector('.yotpo-widget-spotlight-cta__button');
    const label = inner?.textContent.trim();
    if (label) button.setAttribute('aria-label', label);
    // The vendor places a label on a generic inner div. The outer interactive
    // element now carries that name instead.
    inner?.removeAttribute('aria-label');
    if (!keyboardButtons.has(button)) {
      keyboardButtons.add(button);
      button.addEventListener('keydown', (event) => {
        if (event.target !== button || !['Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        button.click();
      });
    }
  }
  for (const host of find('shopify-forms-embed')) repairForm(host);
}

if (typeof document !== 'undefined') {
  const start = () => {
    repairThirdPartyAccessibility(document);
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType === 1) repairThirdPartyAccessibility(node);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}
